import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { variation } from 'ember-launch-darkly';
import { on } from '@ember/modifier';
import { array } from '@ember/helper';
import { fn } from '@ember/helper';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsAlert,
  HdsButton,
  HdsButtonSet,
  HdsCodeBlock,
  HdsFormHelperText,
  HdsFormTextInputField,
  HdsLinkInline,
  HdsSeparator,
} from '@hashicorp/design-system-components/components';
import {
  FlexGrid,
  StickyFooter,
  Typography,
  WithErrors,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import DomainVerification from 'hcp/components/domain-verification';
import SsoBannerAvoidDefaultRoleOrgLockout from 'hcp/components/sso/banner/avoid-default-role-org-lockout';
import SsoBannerDefaultRoleOrgLockoutConfirmation from 'hcp/components/sso/banner/default-role-org-lockout-confirmation';
import SsoFormInputDefaultRoleSelect from 'hcp/components/sso/form-input/default-role-select';
import SsoFormInputDefaultRoleToggleSelect from 'hcp/components/sso/form-input/default-role-toggle-select';

const placeholder = '*****************************************************';

/**
 *
 * `SsoConfigureOidc` is a generic encapsulation of the HTML form
 * and client side validation logic used to configure new OIDC connections, as well as
 * edit existing OIDC configurations.
 *
 * @class SsoConfigureOidc
 *
 */

export default class SsoConfigureOidc extends Component {
  @service config;

  FALLBACK_DEFAULT_ROLE = variation(
    'hcp-identity-sso-default-role-empty-string-no-role'
  )
    ? 'none'
    : 'roles/viewer';

  @tracked clientId = this.args.configuration?.clientId ?? '';
  @tracked clientSecret = this.args.configuration ? placeholder : '';
  @tracked clientSecretIsDisabled = this.args.configuration ? true : false;
  @tracked defaultRole =
    this.args.configuration?.defaultRole ?? this.FALLBACK_DEFAULT_ROLE;
  @tracked issuer = this.args.configuration?.issuer ?? '';

  get callbackUrl() {
    return this.config.app.baseIDPHost + '/sso/oidc/callback';
  }

  handleEditSecretClick = () => {
    if (this.args.configuration && this.clientSecret === placeholder) {
      this.clientSecretIsDisabled = false;
      this.clientSecret = '';

      setTimeout(() => {
        // pushes this to the bottom of the stack. Won't focus the input without timeout
        document.getElementById('client-secret-input').focus();
      }, 0);
    }
  };

  clientSecretBlurred = () => {
    if (this.args.configuration && !this.clientSecret) {
      this.clientSecretIsDisabled = true;
      this.clientSecret = placeholder;
    }
  };

  // the update request body must include an updateMask property
  setRequestBody = (evt) => {
    evt.preventDefault();
    let body;

    if (this.args.configuration) {
      body = {
        updateMask: {
          paths: [],
        },
      };
      if (this.clientId !== this.args.configuration.clientId) {
        body.clientId = this.clientId;
        body.updateMask.paths.push('client_id');
      }

      if (this.issuer !== this.args.configuration.issuer) {
        body.issuer = this.issuer;
        body.updateMask.paths.push('issuer');
      }

      if (this.clientSecret && this.clientSecret !== placeholder) {
        body.clientSecret = this.clientSecret;
        body.updateMask.paths.push('client_secret');
      }

      if (this.args.emailDomains[0] !== this.args.configuration.emailDomain) {
        body.emailDomain = this.args.emailDomains[0];
        body.updateMask.paths.push('email_domain');
      }

      if (variation('hcp-identity-sso-default-role')) {
        body.defaultRole = this.defaultRole;
        body.updateMask.paths.push('default_role');
      }
    } else {
      body = {
        organizationId: this.args.model.org.id,
        clientId: this.clientId,
        issuer: this.issuer,
        emailDomain: this.args.emailDomains[0],
        clientSecret: this.clientSecret,
      };

      if (variation('hcp-identity-sso-default-role')) {
        body.defaultRole = this.defaultRole;
      }
    }

    return this.args.save.perform(body);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <WithErrors
      @error={{or @save.last.error @verifyDomains.last.error}}
      as |FormError FieldError|
    >
      <div data-test-sso-configure>
        <FlexGrid as |Row|>
          <Row.Item @xs="12" @sm="12" @md="12" @lg="8">
            <div {{boxPadding "0 0 lg 0"}}>
              <FormError />
            </div>
            <form
              class="hcp-hds-form"
              autocomplete="off"
              novalidate
              {{on "submit" this.setRequestBody}}
            >
              <DomainVerification
                @domains={{@domains}}
                @domainTxtVerificationRecord={{@model.preconfig.domainTxtVerificationRecord}}
                @emailDomains={{@emailDomains}}
                @errorComponent={{FieldError}}
                @lastDomainIndex={{@lastDomainIndex}}
                @ssoConfigType="Oidc"
                @submittedDomains={{@submittedDomains}}
                @unverifiedDomains={{@unverifiedDomains}}
                @unverifiedReasonsGroupedByDomain={{@unverifiedReasonsGroupedByDomain}}
                @updateDomain={{@updateDomain}}
                @verifiedDomains={{@verifiedDomains}}
                @verifyDomains={{@verifyDomains}}
                @verifiedReasonsGroupedByDomain={{@verifiedReasonsGroupedByDomain}}
              />
              <HdsFormHelperText {{boxPadding "0 0 2xs 0"}}>
                {{t
                  "components.page.organizations.sso.configure.oidc.step-2.subtitle"
                }}
              </HdsFormHelperText>
              <Typography
                @variant="h2"
                class="hds-typography-display-300 hds-font-weight-bold hds-foreground-primary"
                {{boxPadding "0 0 2xs 0"}}
              >
                {{t
                  "components.page.organizations.sso.configure.oidc.step-2.title"
                }}
              </Typography>
              <Typography
                @variant="body1"
                class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                {{boxPadding "0 0 md 0"}}
              >
                {{t
                  "components.page.organizations.sso.configure.oidc.step-2.helper"
                }}
              </Typography>
              <div {{boxPadding "md 0 0 0"}}>
                <div {{boxPadding "0 0 md 0"}}>
                  <FieldError
                    @field="client_id"
                    @hdsDelegatedError={{true}}
                    as |err|
                  >
                    <HdsFormTextInputField
                      @value={{this.clientId}}
                      @isInvalid={{err}}
                      @isRequired={{true}}
                      data-test-input-client-id
                      {{on "input" (pick "target.value" (set this "clientId"))}}
                      as |F|
                    >
                      <F.Label>
                        {{t
                          "components.page.organizations.sso.configure.oidc.step-2.labels.client-id"
                        }}
                      </F.Label>
                      {{#if err}}
                        <F.Error as |E|>
                          <E.Message>
                            {{err.description}}
                          </E.Message>
                        </F.Error>
                      {{/if}}
                    </HdsFormTextInputField>
                  </FieldError>
                </div>
                <div {{boxPadding "md 0"}}>
                  <FieldError
                    @field="client_secret"
                    @hdsDelegatedError={{true}}
                    as |err|
                  >
                    <HdsFormTextInputField
                      @id="client-secret-input"
                      @value={{this.clientSecret}}
                      @isInvalid={{err}}
                      @isRequired={{true}}
                      @type="password"
                      disabled={{this.clientSecretIsDisabled}}
                      autocomplete="new-password"
                      data-test-input-client-secret
                      {{on "blur" this.clientSecretBlurred}}
                      {{on
                        "input"
                        (pick "target.value" (set this "clientSecret"))
                      }}
                      as |F|
                    >
                      <F.Label>
                        {{t
                          "components.page.organizations.sso.configure.oidc.step-2.labels.client-secret"
                        }}
                      </F.Label>
                      <F.HelperText>
                        {{t
                          "components.page.organizations.sso.configure.oidc.step-2.labels.client-secret-helper"
                        }}
                      </F.HelperText>
                      {{#if err}}
                        <F.Error as |E|>
                          <E.Message>
                            {{err.description}}
                          </E.Message>
                        </F.Error>
                      {{/if}}
                    </HdsFormTextInputField>
                    {{#if @configuration}}
                      <HdsButton
                        @icon="edit"
                        @color="tertiary"
                        @text={{t
                          "components.page.organizations.sso.configure.oidc.step-2.labels.client-secret-edit-button"
                        }}
                        data-test-edit-client-secret
                        {{boxPadding "xs sm xs 0"}}
                        {{on "click" this.handleEditSecretClick}}
                      />
                    {{/if}}
                  </FieldError>
                </div>
                <div {{boxPadding "md 0"}}>
                  <FieldError
                    @field="issuer"
                    @hdsDelegatedError={{true}}
                    as |err|
                  >
                    <HdsFormTextInputField
                      @value={{this.issuer}}
                      @isInvalid={{err}}
                      @isRequired={{true}}
                      data-test-input-issuer-url
                      {{on "input" (pick "target.value" (set this "issuer"))}}
                      as |F|
                    >
                      <F.Label>
                        {{t
                          "components.page.organizations.sso.configure.oidc.step-2.labels.issuer-url"
                        }}
                      </F.Label>
                      {{#if err}}
                        <F.Error as |E|>
                          <E.Message>
                            {{err.description}}
                          </E.Message>
                        </F.Error>
                      {{/if}}
                    </HdsFormTextInputField>
                  </FieldError>
                </div>
                <Typography
                  @variant="body1"
                  class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-primary"
                  {{boxPadding "md 0 0 0"}}
                >
                  {{t
                    "components.page.organizations.sso.configure.oidc.step-2.labels.allowed-callback-url"
                  }}
                </Typography>
                <HdsCodeBlock
                  @value={{this.callbackUrl}}
                  @hasCopyButton={{true}}
                  @hasLineNumbers={{false}}
                />
              </div>

              {{#unless (variation "hcp-identity-sso-default-role")}}
                <div {{boxPadding "xl 0 xl 0"}}>
                  <HdsAlert
                    class="additional-information-alert"
                    @type="inline"
                    as |A|
                  >
                    <A.Description>
                      {{t
                        "components.page.organizations.sso.configure.additional-information"
                        htmlSafe=true
                      }}
                    </A.Description>
                  </HdsAlert>
                </div>
              {{/unless}}

              {{#if (variation "hcp-identity-sso-default-role")}}
                <HdsSeparator />

                <section>
                  <HdsFormHelperText {{boxPadding "0 0 2xs 0"}}>
                    {{t
                      "components.page.organizations.sso.configure.oidc.step-3.subtitle"
                    }}
                  </HdsFormHelperText>
                  <Typography
                    @variant="h2"
                    class="hds-typography-display-300 hds-font-weight-bold hds-foreground-primary"
                    {{boxPadding "0 0 2xs 0"}}
                  >
                    {{t
                      "components.page.organizations.sso.configure.oidc.step-3.title"
                    }}
                  </Typography>
                  <Typography
                    @variant="body1"
                    class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                    {{boxPadding "0 0 md 0"}}
                  >
                    {{t
                      "components.page.organizations.sso.configure.oidc.step-3.helper"
                    }}

                    <HdsLinkInline
                      target="_blank"
                      @href="https://developer.hashicorp.com/hcp/docs/hcp/admin/iam/users#user-permissions"
                    >
                      {{t
                        "components.page.organizations.sso.configure.oidc.step-3.helper-link.text"
                      }}
                    </HdsLinkInline>
                  </Typography>

                  <div {{boxMargin "0 0 xl 0"}}>
                    <SsoBannerAvoidDefaultRoleOrgLockout
                      @organization={{@model.org}}
                    />
                  </div>

                  {{#if
                    (variation
                      "hcp-identity-sso-default-role-empty-string-no-role"
                    )
                  }}
                    <SsoFormInputDefaultRoleToggleSelect
                      @currentRolesLocation={{@model.currentRolesLocation}}
                      @currentScopeLink={{@model.currentScopeLink}}
                      @roleOptions={{array
                        "roles/resource-manager.browser"
                        "roles/viewer"
                        "roles/contributor"
                        "roles/admin"
                      }}
                      @selectedRole={{this.defaultRole}}
                      @onRoleToggle={{fn (set this "defaultRole")}}
                      @onRoleSelect={{fn (set this "defaultRole")}}
                    />
                  {{else}}
                    <SsoFormInputDefaultRoleSelect
                      @roleOptions={{array
                        "roles/resource-manager.browser"
                        "roles/viewer"
                        "roles/contributor"
                        "roles/admin"
                      }}
                      @selectedRole={{this.defaultRole}}
                      @handleDefaultRoleChange={{fn
                        (pick "target.value" (set this "defaultRole"))
                      }}
                    />
                  {{/if}}
                </section>
              {{/if}}

              <StickyFooter>
                <HdsButtonSet>
                  <HdsButton
                    @color="primary"
                    @text={{t
                      "components.page.organizations.sso.configure.save"
                    }}
                    disabled={{@save.isRunning}}
                    type="submit"
                    data-test-submit
                  />
                  <HdsButton
                    data-test-cancel
                    @color="secondary"
                    @text={{t
                      "components.page.organizations.sso.configure.cancel"
                    }}
                    @route="cloud.orgs.detail.settings.sso"
                  />
                  {{#unless (eq this.defaultRole "roles/admin")}}
                    <SsoBannerDefaultRoleOrgLockoutConfirmation />
                  {{/unless}}
                </HdsButtonSet>
              </StickyFooter>
            </form>
          </Row.Item>
        </FlexGrid>
      </div>
    </WithErrors>
  </template>
}
